import React, { useState } from 'react';
import {
  Card, Table, Button, Space, Tag, message,
  Typography, Spin, Tooltip, Popconfirm, Alert, Row, Col, Progress
} from 'antd';
import {
  EditOutlined,
  DeleteOutlined,
  HomeOutlined,
  PlusOutlined,
  CheckCircleOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
  AlertOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  useGetLocationsQuery,
  useDeleteLocationMutation,
  useSwitchLocationMutation,
  useSuspendLocationMutation,
  useUnsuspendLocationMutation,
  useGetLocationsSummaryQuery,
  useGetTemperatureAlertsQuery,
} from './location.service';

const { Text, Title } = Typography;

const LocationList = () => {
  const navigate = useNavigate();
  const user = useSelector((state) => state.app.user);
  
  const [loading, setLoading] = useState(false);

  const { data, isLoading, refetch } = useGetLocationsQuery();
  const { data: summaryData, refetch: refetchSummary } = useGetLocationsSummaryQuery();
  const { data: tempData, refetch: refetchTemp } = useGetTemperatureAlertsQuery();
  const [deleteLocation] = useDeleteLocationMutation();
  const [switchLocation] = useSwitchLocationMutation();
  const [suspendLocation] = useSuspendLocationMutation();
  const [unsuspendLocation] = useUnsuspendLocationMutation();

  const locations = data?.data?.locations || [];
  const activeLocationId = data?.data?.activeLocation;
  const maxLocations = data?.data?.maxLocations || 1;
  const summary = summaryData?.data;
  const tempAlerts = tempData?.data;

  const refreshAll = () => {
    refetch();
    refetchSummary();
    refetchTemp();
  };

  const handleDelete = async (id, name) => {
    try {
      await deleteLocation(id).unwrap();
      message.success(`"${name}" deleted successfully`);
      refreshAll();
    } catch (error) {
      message.error(error.data?.message || 'Failed to delete location');
    }
  };

  const handleSwitch = async (id) => {
    try {
      setLoading(true);
      await switchLocation({ locationId: id }).unwrap();
      message.success('Location switched successfully');
      refreshAll();
      const updatedUser = { ...user, activeLocationId: id };
      localStorage.setItem('user', JSON.stringify(updatedUser));
    } catch (error) {
      message.error(error.data?.message || 'Failed to switch location');
    } finally {
      setLoading(false);
    }
  };

  const handleSuspendToggle = async (record) => {
    try {
      setLoading(true);
      if (record.isSuspended) {
        await unsuspendLocation(record._id).unwrap();
        message.success(`"${record.name}" unsuspended`);
      } else {
        await suspendLocation(record._id).unwrap();
        message.success(`"${record.name}" suspended`);
      }
      refreshAll();
    } catch (error) {
      message.error(error.data?.message || 'Failed to update location status');
    } finally {
      setLoading(false);
    }
  };

  const complianceFor = (id) =>
    summary?.locations?.find((l) => l._id === id)?.compliancePercent ?? null;

  const tempFor = (id) =>
    tempAlerts?.locations?.find((l) => l.locationId === id);

  const columns = [
    {
      title: '#',
      key: 'index',
      render: (_, __, index) => index + 1,
      width: 50,
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (name, record) => (
        <Space>
          <HomeOutlined />
          <Text 
            strong 
            style={{ cursor: 'pointer', color: '#1890ff' }}
            onClick={() => navigate(`/locations/${record._id}`)}
          >
            {name}
          </Text>
          {record._id === activeLocationId && (
            <Tag color="green">Active</Tag>
          )}
          {record.isSuspended && <Tag color="red">Suspended</Tag>}
        </Space>
      ),
    },
    {
      title: 'Address',
      dataIndex: 'address',
      key: 'address',
      render: (address) => address?.street || 'N/A',
    },
    {
      title: 'Compliance',
      key: 'compliance',
      width: 140,
      render: (_, record) => {
        const pct = complianceFor(record._id);
        if (pct === null) return '—';
        return <Progress percent={pct} size="small" status={pct < 100 ? 'active' : 'success'} />;
      },
    },
    {
      title: 'Temp alerts (7d)',
      key: 'tempAlerts',
      width: 130,
      render: (_, record) => {
        const t = tempFor(record._id);
        const count = t?.alertCount || 0;
        return count > 0 ? (
          <Tag color="red" icon={<AlertOutlined />}>{count}</Tag>
        ) : (
          <Tag color="green">0</Tag>
        );
      },
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Tag color={record.isSuspended ? 'red' : 'green'}>
            {record.isSuspended ? 'Suspended' : 'Live'}
          </Tag>
          {record.isHolidayMode && <Tag color="orange">Holiday</Tag>}
        </Space>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="small" wrap>
          <Tooltip title="Switch to this location">
            <Button
              type="primary"
              size="small"
              ghost
              icon={<CheckCircleOutlined />}
              onClick={() => handleSwitch(record._id)}
              loading={loading}
              disabled={record._id === activeLocationId || record.isSuspended}
            >
              Switch
            </Button>
          </Tooltip>

          <Tooltip title={record.isSuspended ? 'Unsuspend' : 'Suspend'}>
            <Popconfirm
              title={record.isSuspended ? 'Unsuspend location?' : 'Suspend location?'}
              description={
                record.isSuspended
                  ? 'Staff will be able to use this site again.'
                  : 'Staff cannot use this site until you unsuspend it. Data is kept.'
              }
              onConfirm={() => handleSuspendToggle(record)}
              okText="Confirm"
              cancelText="Cancel"
            >
              <Button
                size="small"
                icon={record.isSuspended ? <PlayCircleOutlined /> : <PauseCircleOutlined />}
              >
                {record.isSuspended ? 'Unsuspend' : 'Suspend'}
              </Button>
            </Popconfirm>
          </Tooltip>

          <Tooltip title="Edit location">
            <Button
              type="default"
              size="small"
              icon={<EditOutlined />}
              onClick={() => navigate(`/locations/edit/${record._id}`)}
            />
          </Tooltip>

          <Tooltip title="Delete location">
            <Popconfirm
              title="Delete Location"
              description={`Are you sure you want to delete "${record.name}"? This will also delete all staff and checklists associated with this location.`}
              onConfirm={() => handleDelete(record._id, record.name)}
              okText="Yes, Delete"
              cancelText="Cancel"
              okButtonProps={{ danger: true }}
            >
              <Button
                danger
                size="small"
                icon={<DeleteOutlined />}
                disabled={record._id === activeLocationId}
              />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      {summary && (
        <Row gutter={16}>
          <Col xs={24} md={8}>
            <Card size="small">
              <Text type="secondary">Overall compliance</Text>
              <Title level={3} style={{ margin: '4px 0 0' }}>
                {summary.overallCompliance}%
              </Title>
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card size="small">
              <Text type="secondary">Sites with outstanding tasks</Text>
              <Title level={3} style={{ margin: '4px 0 0' }}>
                {summary.outstandingSites}
              </Title>
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card size="small">
              <Text type="secondary">Temp alerts (7 days)</Text>
              <Title level={3} style={{ margin: '4px 0 0', color: (tempAlerts?.totalAlerts || 0) > 0 ? '#cf1322' : undefined }}>
                {tempAlerts?.totalAlerts ?? 0}
              </Title>
            </Card>
          </Col>
        </Row>
      )}

      {(tempAlerts?.totalAlerts || 0) > 0 && (
        <Alert
          type="warning"
          showIcon
          icon={<AlertOutlined />}
          message="Temperature alerts across locations"
          description={
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {(tempAlerts.locations || [])
                .filter((l) => l.alertCount > 0)
                .map((l) => (
                  <li key={l.locationId}>
                    <strong>{l.locationName}</strong>: {l.alertCount} alert(s)
                    {l.latestAlerts?.[0] ? ` — latest: ${l.latestAlerts[0].reason}` : ''}
                  </li>
                ))}
            </ul>
          }
        />
      )}

      <Card
        bordered={false}
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '18px', fontWeight: 'bold' }}>
              Locations
            </span>
            <Tag color="blue">{locations.length} / {maxLocations} locations</Tag>
            {summary?.suspendedCount > 0 && (
              <Tag color="red">{summary.suspendedCount} suspended</Tag>
            )}
          </div>
        }
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate('/account?panel=addLocation')}
          >
            Add Location
          </Button>
        }
      >
        <Spin spinning={isLoading}>
          <Table
            dataSource={locations}
            columns={columns}
            rowKey="_id"
            pagination={{ pageSize: 10 }}
          />

          {locations.length === 0 && !isLoading && (
            <div style={{ textAlign: 'center', padding: '60px' }}>
              <HomeOutlined style={{ fontSize: 48, color: '#d9d9d9' }} />
              <p style={{ marginTop: 12, color: '#999' }}>
                No locations found.
                <br />
                <Button
                  type="link"
                  onClick={() => navigate('/account?panel=addLocation')}
                >
                  Add a location from My Account
                </Button>
              </p>
            </div>
          )}
        </Spin>
      </Card>
    </Space>
  );
};

export default LocationList;
